import { ApiError } from "@jobenvoy/core";
import { describeError } from "@/lib/api/client";
import type { TailorRequested } from "@/lib/api/envoy";
import type { Resume } from "@/lib/api/resumeService";
import { clockTime } from "@/lib/overview";
import type { PastedJob } from "./pastedJob";

/** A tailor that ended with no resume and no reason of its own. */
export const TAILOR_STOPPED = "This tailor did not finish. Try again.";
/** Past the wait: the tailor may still end, so the page says where it will show. */
export const TAILOR_SLOW =
    "This is taking longer than it should. Your Applications page will have it when it is done.";
/** Past the wait with no answer: the tailor was never reached, so nothing was started. */
export const TAILOR_UNREACHED =
    "We could not reach the tailor. Check your connection and try again.";
/** A saved resume with nothing for a tailor to work from. */
export const RESUME_EMPTY = "Your resume has no work history yet. Add it, then tailor.";
/** The upload dialog's code would not load. */
export const UPLOAD_UNLOADED = "The resume upload did not open. Reload the page and try again.";
/** A link `jobAddress` passed and envoy did not, once it had put the query in order. */
export const LINK_REFUSED =
    "That link did not work. Paste the job's own https address, with nothing extra on the end.";

/** A posting that closed after this page read it. */
export const POSTING_CLOSED = "This job has closed, so there is nothing to tailor for it.";
/** A posting whose application was sent, declined or closed. */
export const APPLICATION_DONE = "You are done with this job. Your Applications page has it.";

/** Envoy's refusal of a page's address: `not_a_page` in `models/page.rs`. */
const NOT_A_PAGE = "not an https page's address";

/**
 * The end of envoy's refusal of a closed posting, `posting <id> has closed`,
 * on both routes. apps/envoy/tests/refused_words.rs pins it.
 */
const CLOSED_TAIL = " has closed";

/** A job back for the form to hold: `problem` is what is wrong with its link, when it is. */
export interface KeptJob {
    job: PastedJob;
    problem: string | null;
}

/** Where a tailor goes from here: a person with no saved resume has nothing to tailor yet. */
export function nextStep(hasBaseline: boolean): "upload" | "tailor" {
    return hasBaseline ? "tailor" : "upload";
}

/**
 * Whether a saved resume gives a tailor anything to work from. A read can
 * come back with a name and nothing else, and a tailor costs a credit.
 */
export function hasWorkHistory(resume: Resume | null | undefined): boolean {
    if (!resume) return false;
    return resume.experience.length > 0 || resume.skills.length > 0 || resume.education.length > 0;
}

/** What stopped the tailor, in words. Null for no stop, and for one with no words of its own. */
export function waitingLine(requested: TailorRequested, timeZone?: string): string | null {
    switch (requested.waiting_on) {
        case "SLOWED":
            return "It is in your queue and will be written in a few minutes.";
        case "BUSY":
            return "Another of your tailors is being written. Try again when it is done.";
        case "RETRY_LATER": {
            const after = requested.retry_after;
            return after && !Number.isNaN(Date.parse(after))
                ? `This tailor failed three times. Try again after ${clockTime(after, timeZone)}`
                : "This tailor failed three times. Try again later.";
        }
        default:
            return null;
    }
}

/** Whether a refused tailor was refused for its link. */
export function isLinkRefusal(error: unknown): boolean {
    return error instanceof ApiError && error.status === 400 && describeError(error) === NOT_A_PAGE;
}

/**
 * Whether a posting was refused for having closed. A 412 can also mean no
 * saved resume, so only envoy's words tell the two apart.
 */
export function isClosedRefusal(error: unknown): boolean {
    return (
        error instanceof ApiError &&
        error.status === 412 &&
        describeError(error).endsWith(CLOSED_TAIL)
    );
}

/**
 * Why a posting's tailor was refused, for a refusal envoy words for a log:
 * it names ids and states. Null for one whose own words will do. There is
 * no link for the person to fix on a posting, so a refused link is a
 * tailor that did not start.
 */
export function postingRefusal(error: unknown): string | null {
    if (!(error instanceof ApiError)) return null;
    if (isClosedRefusal(error)) return POSTING_CLOSED;
    if (error.status === 409) return APPLICATION_DONE;
    return isLinkRefusal(error) ? TAILOR_STOPPED : null;
}
